import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Link2, Share2 } from 'lucide-react';
import { getSessionById, getMessagesBySessionId, addMessageToSession } from '../lib/supabaseService';
import type { Database } from '../lib/database.types';

type Session = Database['public']['Tables']['sessions']['Row'];
type Message = Database['public']['Tables']['messages']['Row'];

const SessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const sessionUrl = `${window.location.origin}/session/${sessionId}`;

  const getInspirationMessage = (context: string) => {
    switch (context.toLowerCase()) {
      case 'associés':
      case 'associés/collègues':
        return '💡 **Besoin d\'inspiration ?**\nDécrivez votre environnement de travail, ce que vous pensez de votre associé, les tensions que vous rencontrez, ses attitudes, vos attentes ou propositions d\'amélioration.';
      case 'couple':
        return '💡 **Besoin d\'inspiration ?**\nExprimez vos ressentis dans la relation, les moments où vous vous sentez incompris(e), ce qui vous blesse ou vous fait douter. Parlez librement, sans jugement.';
      case 'amis':
        return '💡 **Besoin d\'inspiration ?**\nParlez de ce qui a changé dans votre amitié, d\'un malentendu ou d\'un événement qui vous a blessé(e). Vous pouvez aussi évoquer ce que cette relation représente pour vous.';
      case 'voisins':
        return '💡 **Besoin d\'inspiration ?**\nDécrivez la situation concrète (bruit, incivilité, espace partagé…), vos ressentis et ce que vous espérez comme changement pour retrouver une bonne entente.';
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchSessionData = async () => {
      if (!sessionId) return;

      try {
        const [sessionData, messagesData] = await Promise.all([
          getSessionById(sessionId),
          getMessagesBySessionId(sessionId)
        ]);

        setSession(sessionData);
        setMessages(messagesData);

        // If we already have 2 messages, redirect to synthesis
        if (messagesData.length >= 2) {
          navigate(`/synthesis/${sessionId}`);
        }
      } catch (err) {
        setError('Impossible de charger les données de la session');
        console.error('Error fetching session data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId, navigate]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(sessionUrl);
      setCopied(true);
      setShareError(null);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setShareError('Impossible de copier le lien. Veuillez le sélectionner manuellement.');
    }
  };

  const handleShare = async () => {
    // Clear any previous share errors
    setShareError(null);

    // Check if the Web Share API is available
    if (!navigator.share) {
      setShareError('Le partage n\'est pas supporté par votre navigateur. Veuillez copier le lien manuellement.');
      return;
    }

    try {
      await navigator.share({
        title: 'Médiation',
        text: 'Rejoignez-moi pour une médiation bienveillante',
        url: sessionUrl
      });
    } catch (err) {
      console.error('Error sharing:', err);
      
      // Handle specific error cases
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          // User cancelled sharing - no need to show an error
          return;
        }
        if (err.name === 'NotAllowedError') {
          setShareError('Le partage a été bloqué. Veuillez utiliser le bouton "Copier" pour partager le lien.');
          return;
        }
      }
      
      // Generic error message for other cases
      setShareError('Une erreur est survenue lors du partage. Veuillez utiliser le bouton "Copier" à la place.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || !message.trim()) return;

    setIsSending(true);
    setError(null);

    try {
      // Determine user order (1 if no messages, 2 if one message exists)
      const userOrder = messages.length === 0 ? 1 : 2;
      
      await addMessageToSession(sessionId, userOrder, message);
      
      // If this was the second message, redirect to synthesis
      if (userOrder === 2) {
        navigate(`/synthesis/${sessionId}`);
      } else {
        // Refresh messages
        const updatedMessages = await getMessagesBySessionId(sessionId);
        setMessages(updatedMessages);
        setMessage('');
      }
    } catch (err) {
      setError('Une erreur est survenue lors de l\'envoi du message');
      console.error('Error sending message:', err);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse text-slate-600">Chargement...</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center text-error p-4">
        {error || 'Session non trouvée'}
      </div>
    );
  }

  const inspirationMessage = getInspirationMessage(session.context);

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="card mb-8">
        <h1 className="text-2xl font-bold mb-4">
          {messages.length === 0 ? 'Exprimez votre point de vue' : 'Partagez votre vision'}
        </h1>
        <div className="space-y-2 text-slate-600">
          <p><strong>Contexte :</strong> {session.context}</p>
          <p><strong>Sujet :</strong> {session.topic}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
            Votre message
          </label>
          {inspirationMessage && (
            <div className="text-sm text-muted-foreground italic bg-gray-50 p-3 rounded-xl mt-2 whitespace-pre-line">
              {inspirationMessage}
            </div>
          )}
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Exprimez-vous librement..."
            className="textarea-field mt-2"
            rows={6}
            disabled={isSending}
          />
          <p className="mt-2 text-sm text-muted-foreground">
            💡 Petite astuce : vous pouvez utiliser la dictée vocale de votre téléphone pour dicter votre message plus rapidement.
          </p>
        </div>

        {error && (
          <div className="text-error text-sm p-3 bg-red-50 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isSending || !message.trim()}
            className="btn-primary text-lg px-8 py-4 rounded-3xl flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 bg-gradient-to-r from-sky-500 to-sky-400 min-w-[200px]"
          >
            <Send size={20} />
            {isSending ? 'Envoi en cours...' : 'Envoyer votre message'}
          </button>
        </div>
      </form>

      <div className="mt-12 bg-sky-50 rounded-3xl p-8 border border-sky-100 shadow-sm">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-sky-900 mb-2">Partagez avec votre interlocuteur</h2>
          <p className="text-sky-700">
            Quand il aura répondu, ce même lien affichera la synthèse rédigée par Léo. Conservez-le !
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-center gap-2">
            <input
              type="text"
              value={sessionUrl}
              readOnly
              className="input-field text-sm bg-white/80"
            />
            <button
              onClick={handleCopyLink}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
            >
              <Link2 size={18} />
              {copied ? 'Copié !' : 'Copier'}
            </button>
            {navigator.share && (
              <button
                onClick={handleShare}
                className="btn-primary flex items-center gap-2 whitespace-nowrap"
              >
                <Share2 size={18} />
                Partager
              </button>
            )}
          </div>
          
          {shareError && (
            <div className="text-sm text-rose-600 text-center bg-rose-50 p-2 rounded-lg">
              {shareError}
            </div>
          )}
        </div>
      </div>

      <p className="mt-8 text-center text-sm text-slate-600">
        Votre message ne sera jamais envoyé directement à votre interlocuteur. Il sera utilisé pour générer une synthèse bienveillante afin de résoudre le conflit.
      </p>
    </div>
  );
};

export default SessionPage;